'use client';

import React, { useState, createContext, useContext, useEffect, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import BondsRepository from '@/creditflowresearch/infrastructure/repository/BondsRepository';
import { getContainer } from '@framework/di/getContainer';
import usePrevious from '@framework/hooks/usePrevious';
import { IBwicItem, ISubscriptionStatus } from '@/creditflowresearch/domain/vo/Bond/IBonds';
import { IDatetime } from '@framework/date-helpers/Datetime';
import { useBondsAssetClassesContext } from '@/creditflowresearch/application/Bonds/BondsAssetClassesContext';
import { useFiltersContext } from '@/creditflowresearch/application/FiltersContext';
import useGetBondsPayload from '@/creditflowresearch/application/Bonds/useGetBondsPayload';
import { useBondsFiltersContext } from '@/creditflowresearch/application/Bonds/BondsFiltersContext';

interface IBwics {
  collection: IBwicItem[],
  totalCount: number,
}

export interface IBwicsData {
  bwics?: IBwics,
  lastImportAt?: IDatetime,
  subscriptionStatus?: ISubscriptionStatus,
}

export const useBwicTable = (initial: IBwicsData, isTile?: boolean) => {
  const [bwicsData, setBwicsData] = useState<IBwicsData>(initial);
  const [isBwicFetching, setIsBwicFetching] = useState(false);
  const container = getContainer();
  const { filtersPayload } = useBondsFiltersContext();
  const { currentAssetClass } = useBondsAssetClassesContext();
  const { params } = useFiltersContext();
  const prevParams = usePrevious({ ...params, ...filtersPayload }) || { ...params, ...filtersPayload };

  const { getPayload } = useGetBondsPayload();

  useEffect(() => {
    const currentParams = { ...params, ...filtersPayload };
    const shouldDenyFetch = (!currentAssetClass || !isTile || !filtersPayload)
                            && isEqual(prevParams, currentParams);
    if (shouldDenyFetch) return;
    getBwics(getPayload({}));
  }, [
    params.perPage,
    params.page,
    params.sortBy,
    params.sortOrder,
    currentAssetClass,
    filtersPayload,
  ]);

  useEffect(() => setBwicsData(initial), [initial]);

  const getBwics = async (payload) => {
    setIsBwicFetching(true);

    try {
      const bondsRepository = container.get(BondsRepository);
      const response = await bondsRepository.getBwicList(payload);
      setBwicsData(response);
    } catch (e) {
      if (Dispatcher.isAbortError(e)) {
        return;
      }
    } finally {
      setIsBwicFetching(false);
    }
  };

  const debouncedGetBwics = useCallback(debounce(getBwics, 500), []);

  return {
    isBwicFetching,
    getBwics,
    setBwicsData,
    bwicsData,
    debouncedGetBwics,
  };
};

type BwicTableContextType = ReturnType<typeof useBwicTable>;

export const BwicTableContext = createContext<BwicTableContextType>(null);

export const useBwicTableContext = () => {
  const context = useContext(BwicTableContext);
  if (!context) {
    throw new Error('useBwicTableContext must be used within the BwicTableContext');
  }
  return context;
};

interface IProps {
  initial: IBwicsData,
  isTile?: boolean,
  children: React.ReactNode,
}

const BwicTableContextProvider = ({ initial, isTile, children }: IProps) => (
  <BwicTableContext.Provider value={ useBwicTable(initial, isTile) }>
    { children }
  </BwicTableContext.Provider>
);

export default BwicTableContextProvider;
